<template>
  <div class="offer-container" v-if="!contentLoading">
    <div class="offer-managed-contents">
      <img v-if="headerImage" :src="headerImage" alt="Output One banner" class="header-image" />
      <ScreenHeading>{{ title }}</ScreenHeading>
      <div v-for="({ heading, list }, index) in contentSections" :key="index" class="list-item">
        <h3 v-if="heading">{{ heading }}</h3>
        <ul>
          <li v-for="listItem in list" :key="listItem">{{ listItem }}</li>
        </ul>
      </div>
    </div>
    <PaymentDetails />
    <div class="offer-footer">
      <div class="plan-info">
        <p>
          <strong>{{ planPaymentSpecifics }}</strong>
        </p>
        <p class="subheading">{{ cancellationReminder }} Cancel anytime.</p>
      </div>
      <p class="fineprint">
        By clicking {{ buttonText }} you agree to Output’s <a :href="termsUrl" target="_blank">Terms of Use</a>,
        <a :href="privacyPolicyUrl" target="_blank">Privacy Policy</a>, and that you will automatically be charged the membership fee ({{ planPrice }}/{{
          planInterval
        }}
        pre-tax) {{ trialDetails }} until you cancel. You may cancel anytime by going to your Output Account and clicking "Cancel Subscription."
      </p>
      <ScreenNextSectionBtn :text="buttonText" hide-enter @navigateClick="submitOffer" class="submit-button" :disabled="!hasValidPaymentDetails" />
      <img class="icons" src="https://storyblok-assets.output.com/896x80/10a4a3e8bf/trust-badges.png" width="448" height="40" alt="" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAmplitude, BrazeEvents, useGtm } from '@outputinc/o-analytics'
import PaymentDetails from '@/components/shared/payment-details/PaymentDetails.vue'
import ScreenHeading from '@/components/shared/screens/ScreenHeading.vue'
import ScreenNextSectionBtn from '@/components/shared/screens/ScreenNextSectionBtn.vue'

import { useBrazeStore } from '@/store/BrazeStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useErrorsStore } from '@/store/ErrorsStore'
import { useModalStore } from '@/store/ModalStore'
import { useProductStore } from '@/store/ProductStore'
import { useRecurlyStore } from '@/store/RecurlyStore'
import { useScreenStore } from '@/store/ScreenStore'
import { useUserStore } from '@/store/UserStore'
import { useOfferStore } from '@/store/OfferStore'
import { usePlanStore } from '@/store/PlanStore'
import { useSubscriptionStore } from '@/store/SubscriptionStore'
import { useCouponStore } from '@/store/CouponStore'
import { usePriceStore } from '@/store/PriceStore'
import { useContentStore } from '@/store/ContentStore'

const recurlyStore = useRecurlyStore()
const userStore = useUserStore()
const checkoutStore = useCheckoutStore()
const errorsStore = useErrorsStore()
const offerStore = useOfferStore()
const planStore = usePlanStore()
const subscriptionStore = useSubscriptionStore()
const couponStore = useCouponStore()
const priceStore = usePriceStore()

const { handleTransactionError, submitCreateSubscription, onCreateSubscription, process3DSecure } = recurlyStore
const { setCheckoutProperty } = checkoutStore
const { country, currency, firstName: billingFirstName, lastName: billingLastName, postalCode, paymentDetails } = storeToRefs(checkoutStore)
const { push: pushError, setAppError } = errorsStore
const { trigger: triggerModal } = useModalStore()
const { paymentMethod, token: recurlyToken } = storeToRefs(recurlyStore)
const { email, country: userCountry, outputUid, isDirectResubscriber, isAnyOutputResubscriber, userType } = storeToRefs(userStore)
const productStore = useProductStore()
const { productName } = storeToRefs(productStore)
const { trackEmailUser } = useBrazeStore()
const { setScreenComplete, navigateNext, setBusy } = useScreenStore()
const { track, Events } = useAmplitude()
const { selectedPlanProduct, selectedPlanIntervalAsNoun, selectedPlanTrialLengthDisplay } = storeToRefs(planStore)
const { activeOfferPaymentProductDisplay, activeOfferId, postPromotionalOfferPlanInterval } = storeToRefs(offerStore)
const { mostRecentSubscriptionIntervalAsNoun, mostRecentSubscriptionInterval } = storeToRefs(subscriptionStore)
const { removeCoupon } = couponStore
const { appliedCouponTrial, appliedCouponTrialLength } = storeToRefs(couponStore)
const { selectedPlanDisplayPrice, postPromoOfferPlanDisplayPrice } = storeToRefs(priceStore)
const contentStore = useContentStore()
const { headerImage, headerTitle: title, contentSections, ctaText: buttonText, loading: contentLoading } = storeToRefs(contentStore)

const { track: gtmTrackEvent, Events: GtmEvents } = useGtm()

defineProps<{
  submitHandler: () => void
}>()

const isSubmitting = ref(false)

const planPrice = computed(() => (postPromoOfferPlanDisplayPrice.value ? postPromoOfferPlanDisplayPrice?.value : selectedPlanDisplayPrice?.value))
const trialLength = computed(() => (appliedCouponTrial.value ? appliedCouponTrialLength.value : selectedPlanTrialLengthDisplay.value))
const trialDetails = computed(() => (trialLength.value ? `after a ${trialLength.value} trial` : ''))
const planIntervalAsNoun = computed(() => (activeOfferId.value ? mostRecentSubscriptionIntervalAsNoun.value : selectedPlanIntervalAsNoun.value))
const planInterval = computed(() => (postPromotionalOfferPlanInterval.value ? mostRecentSubscriptionInterval.value : postPromotionalOfferPlanInterval.value))
const paymentProductDisplay = computed(() => (activeOfferPaymentProductDisplay.value ? activeOfferPaymentProductDisplay.value : productName.value))
const planPaymentSpecifics = computed(
  () => `${paymentProductDisplay.value} - ${planPrice.value}/${planIntervalAsNoun.value} (pre-tax) ${trialDetails.value ? `${trialDetails.value}.` : ''}`
)
const cancellationReminder = computed(() => (trialLength.value ? "We'll remind you three days before your trial ends." : ''))

const directResubscriberProduct = computed(() => (isDirectResubscriber.value ? selectedPlanProduct.value : 'none'))

const termsUrl = `${import.meta.env.VITE_OUTPUT_DOT_COM_HOSTNAME}/legal/terms`
const privacyPolicyUrl = `${import.meta.env.VITE_OUTPUT_DOT_COM_HOSTNAME}/legal/privacy`
const hasValidPaymentDetails = computed(() => !!paymentDetails.value)

const submitOffer = () => {
  if (isSubmitting.value) {
    return // prevent double submission
  }
  isSubmitting.value = true

  submitCreateSubscription()
}

const handleError = (error: any) => {
  isSubmitting.value = false
  setBusy(false)

  if (error.type === 'validation') {
    if (error.message.includes('Subscription has reached max redemptions for this account')) {
      setAppError({
        title: 'Coupon code is not applicable',
        message: 'Your coupon code has already been redeemed or is not applicable to this plan.'
      })
      removeCoupon()
    } else {
      pushError(error)
      triggerModal({ error })
    }
  } else if (error.type === 'transaction' && error.transactionError?.code === 'three_d_secure_action_required') {
    process3DSecure(error.transactionError.threeDSecureActionTokenId)
  } else {
    handleTransactionError()
    pushError(error)
    triggerModal({ error })
  }
}

const handleResponse = (payload: any) => {
  const { error, ...response } = payload

  if (error) {
    handleError(error)
  } else {
    const { uuid, plan, trialStartedAt } = response
    const isTrial = !!trialStartedAt

    gtmTrackEvent(GtmEvents.CHECKOUT_SUBSCRIPTION_SUCCESS_LEGACY, {
      subscription_id: uuid,
      directResubscriber: directResubscriberProduct.value,
      anyOutputResubscriber: isAnyOutputResubscriber.value,
      isTrial,
      outputUid: outputUid.value
    })

    trackEmailUser({
      externalId: outputUid.value,
      name: BrazeEvents.CHECKOUT_SUBSCRIPTION_COMPLETE,
      time: new Date().toISOString(),
      properties: {
        billing_interval: planInterval.value,
        plan_code: plan.code,
        plan_price: planPrice.value,
        products: selectedPlanProduct.value
      }
    })

    track(Events.CHECKOUT_SUBSCRIPTION_SUCCESS, {
      anyOutputResubscriber: isAnyOutputResubscriber.value,
      directResubscriber: directResubscriberProduct.value,
      billingCountry: country.value,
      currency: currency.value,
      detectedCountry: userCountry.value,
      email: email.value,
      firstName: billingFirstName.value,
      id: outputUid.value,
      lastName: billingLastName.value,
      paymentMethod: paymentMethod.value,
      planCode: plan.code,
      postalCode: postalCode.value,
      products: [selectedPlanProduct.value],
      recurlyToken: recurlyToken.value,
      userType: userType.value,
      uuid,
      isTrial
    })

    setScreenComplete(true)

    setCheckoutProperty({ isComplete: true })
    navigateNext()
  }
}

onCreateSubscription((subscriptionResponse: any) => {
  if (!subscriptionResponse?.data) {
    isSubmitting.value = false
    setBusy(false)
    return
  }

  const {
    data: { createSubscription }
  } = subscriptionResponse
  handleResponse(createSubscription)
})
</script>

<style scoped lang="scss">
.hidden {
  display: none;
}

.offer-container {
  max-width: 345px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: $spacer-3 auto;
  padding: $spacer-3;

  .screen-next-section-btn {
    width: 100%;
  }

  @include media-breakpoint(sm) {
    max-width: 576px;
    margin: $spacer-6 auto;
    padding: $spacer-5;
  }

  .header-image {
    max-width: 100%;
    margin-bottom: $spacer-4;

    @include media-breakpoint(sm) {
      margin-bottom: $spacer-6;
    }
  }

  li {
    margin-bottom: $spacer-4;
  }

  .list-item {
    width: 100%;
  }

  .plan-info {
    text-align: center;
    margin: $spacer-4 0;

    p {
      margin-bottom: 0;
    }

    .subheading {
      font-family: $font-family-pilat-regular;
    }
  }

  .offer-footer {
    display: flex;
    flex-direction: column;
    align-items: center;

    .fineprint {
      margin-bottom: 0;
      font-size: 10px;
      line-height: $spacer-3;
      letter-spacing: 0.4px;
    }

    .submit-button {
      width: 100%;
    }

    .icons {
      margin-top: $spacer-5;

      @include media-breakpoint(sm) {
        margin-top: $spacer-6;
      }
    }
  }
}
</style>
